import { useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { Tabs, TabType } from './components/Tabs';
import { UrlInput } from './components/UrlInput';
import { YouTubeInput } from './components/YouTubeInput';
import { MarkdownPreview } from './components/MarkdownPreview';
import { ScrapingStatus } from './components/ScrapingStatus';
import { PageList } from './components/PageList';
import { RefineTab } from './components/refine/RefineTab';
import { UserMenu } from './components/UserMenu';
import { Logo } from './components/Logo';
import { UsageLimitModal } from './components/UsageLimitModal';
import { ProtectedRoute } from './components/ProtectedRoute';
import { PricingPage } from './pages/PricingPage';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { AuthCallback } from './pages/AuthCallback';
import { ResetPassword } from './pages/ResetPassword';
import { AccountSettings } from './pages/AccountSettings';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { scrapeUrl, UsageLimitError } from './lib/api';

interface Page {
  url: string;
  title: string;
  markdown: string;
}

function App() {
  const [activeTab, setActiveTab] = useState<TabType>('web');
  const [pages, setPages] = useState<Page[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usageLimitInfo, setUsageLimitInfo] = useState<{
    tier: string;
    used: number;
    limit: number;
    periodEnd: string;
  } | null>(null);

  const handleApiError = (err: unknown) => {
    if (err instanceof UsageLimitError) {
      setUsageLimitInfo({
        tier: err.tier,
        used: err.used,
        limit: err.limit,
        periodEnd: err.periodEnd,
      });
    } else {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleScrape = async (url: string, includeImages: boolean) => {
    setIsLoading(true);
    setError(null);
    setPages([]);
    setSelectedIndex(0);

    try {
      const data = await scrapeUrl(url, includeImages);

      if ('pages' in data) {
        setPages(data.pages);
      } else {
        setPages([data]);
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFetchTranscript = async (url: string) => {
    setIsLoading(true);
    setError(null);
    setPages([]);
    setSelectedIndex(0);

    try {
      const data = await scrapeUrl(url, false);

      if ('pages' in data) {
        setPages(data.pages);
      } else {
        setPages([data]);
      }
    } catch (err) {
      handleApiError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    if (pages.length === 0) return;

    const currentPage = pages[selectedIndex];
    const filename = currentPage.title
      ? `${currentPage.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.md`
      : 'content.md';

    const blob = new Blob([currentPage.markdown], { type: 'text/markdown;charset=utf-8' });
    saveAs(blob, filename);
  };

  const handleDownloadAll = async () => {
    if (pages.length === 0) return;

    if (pages.length === 1) {
      handleDownload();
      return;
    }

    const zip = new JSZip();

    pages.forEach((page, index) => {
      const filename = page.title
        ? `${String(index + 1).padStart(2, '0')}-${page.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.md`
        : `page-${index + 1}.md`;
      zip.file(filename, page.markdown);
    });

    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, 'distyll-pages.zip');
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setPages([]);
    setError(null);
    setSelectedIndex(0);
  };

  const currentPage = pages[selectedIndex];

  const mainContent = (
    <div className="min-h-screen bg-surface-950">
      {/* Main content */}
      <div className="flex flex-col min-h-screen p-6 lg:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <header className="mb-8 animate-fade-in">
          <div className="flex items-center justify-between mb-3">
            <Logo />

            {/* User Menu */}
            <div className="flex items-center gap-3">
              <UserMenu />
            </div>
          </div>
        </header>

        {/* Usage Limit Modal */}
        {usageLimitInfo && (
          <UsageLimitModal
            tier={usageLimitInfo.tier}
            used={usageLimitInfo.used}
            limit={usageLimitInfo.limit}
            periodEnd={usageLimitInfo.periodEnd}
            onClose={() => setUsageLimitInfo(null)}
          />
        )}

        {/* Tabs */}
        <div className="mb-6">
          <Tabs activeTab={activeTab} onTabChange={handleTabChange} />
        </div>

        {/* Content Section - Conditional based on tab */}
        {activeTab === 'refine' ? (
          <div className="animate-fade-in">
            <RefineTab />
          </div>
        ) : (
          <>
            {/* Input Section */}
            <div className="mb-6 animate-fade-in">
              <div className="card p-6">
                {activeTab === 'web' ? (
                  <UrlInput onScrape={handleScrape} isLoading={isLoading} />
                ) : (
                  <YouTubeInput onFetchTranscript={handleFetchTranscript} isLoading={isLoading} />
                )}
              </div>
            </div>

            {/* Status */}
            <div className="animate-fade-in">
              <ScrapingStatus
                isLoading={isLoading}
                error={error}
                pageCount={pages.length}
              />
            </div>

            {/* Content Area */}
            {pages.length > 0 && (
              <div className="flex gap-5 mt-6 flex-1 min-h-0 animate-slide-up">
                <PageList
                  pages={pages}
                  selectedIndex={selectedIndex}
                  onSelect={setSelectedIndex}
                />
                <MarkdownPreview
                  content={currentPage?.markdown || ''}
                  title={currentPage?.title}
                />
              </div>
            )}

            {pages.length === 0 && !isLoading && !error && (
              <div className="flex-1 mt-6 animate-fade-in">
                <MarkdownPreview content="" />
              </div>
            )}

            {/* Download Actions */}
            {pages.length > 0 && (
              <div className="flex gap-4 mt-6 animate-slide-up">
                <button
                  onClick={handleDownload}
                  className="btn-secondary flex items-center gap-2.5"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Download .md
                </button>
                {pages.length > 1 && (
                  <button
                    onClick={handleDownloadAll}
                    className="btn-primary flex items-center gap-2.5"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                      />
                    </svg>
                    Download All (.zip)
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <footer className="mt-auto pt-8">
          <div className="text-center text-surface-500 text-sm">
            <p>Built for developers who value clean documentation</p>
          </div>
        </footer>
      </div>
    </div>
  );

  return (
    <Routes>
      <Route path="/" element={<ProtectedRoute>{mainContent}</ProtectedRoute>} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      <Route path="/account" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
    </Routes>
  );
}

export default App;
